import { useEffect, useRef } from 'react';
import { useWebviewBridge, useWebviewEvents } from '@contexts/webview-bridge';

const MAIN_SCROLL_ATTR = 'data-main-scroll';

/**
 * Communicates whether Android's native pull-to-refresh should be active.
 *
 * Only active inside the Android webview (detected via `globalThis.Android`).
 * On browser and iOS the hook is a no-op: no listeners are attached.
 *
 * PTR is enabled only when the user is interacting with the main page content
 * at the top — not in modals, drawers, popovers, or any other scrollable
 * container (tables, lists, sidebars, etc.).
 *
 * Decision is made at touch start (so native knows before the gesture moves)
 * and refreshed on scroll inside the main container.
 */
export const useAndroidSwipeRefresh = () => {
  const lastValueRef = useRef<boolean | null>(null);
  const { sendPullToRefreshEnabled } = useWebviewEvents();
  const { isInitialized } = useWebviewBridge();

  useEffect(() => {
    if ((globalThis as { Android?: unknown }).Android === undefined) return;
    // Wait until the bridge is ready. Otherwise `send` is the pre-init stub
    // that throws, the initial "enabled=true" ping is swallowed, and
    // `lastValueRef` has already been flipped, so the re-run after init
    // early-returns and native never hears from us.
    if (!isInitialized) return;

    const update = (enabled: boolean) => {
      if (lastValueRef.current === enabled) return;
      lastValueRef.current = enabled;
      sendPullToRefreshEnabled(enabled).catch((): void => undefined);
    };

    const isScrollable = (el: HTMLElement) => {
      const overflowY = getComputedStyle(el).overflowY;
      return (overflowY === 'auto' || overflowY === 'scroll') && el.scrollHeight > el.clientHeight;
    };

    const resolveEnabledForTarget = (target: Element): boolean => {
      // Any open dialog / drawer / popover disables PTR
      if (target.closest('[role="dialog"]')) return false;

      let el: HTMLElement | null = target instanceof HTMLElement ? target : target.parentElement;
      while (el) {
        if (isScrollable(el)) {
          if (!el.hasAttribute(MAIN_SCROLL_ATTR)) return false;
          return el.scrollTop === 0;
        }
        el = el.parentElement;
      }
      return false;
    };

    let rafId: number | null = null;

    const handleTouchStart = (event: TouchEvent) => {
      const target = event.target;
      if (!(target instanceof Element)) return;
      update(resolveEnabledForTarget(target));
    };

    const handleScroll = (event: Event) => {
      if (rafId !== null) return;

      rafId = requestAnimationFrame(() => {
        rafId = null;
        const target = event.target;

        if (!(target instanceof HTMLElement)) return;
        // While any dialog is open, do not emit enable=true from residual
        // scroll events (momentum / programmatic) on the main container —
        // the modal is on top and PTR must stay disabled.
        if (document.querySelector('[role="dialog"]')) return;
        if (!target.hasAttribute(MAIN_SCROLL_ATTR)) return;

        update(target.scrollTop === 0);
      });
    };

    document.addEventListener('touchstart', handleTouchStart, { capture: true, passive: true });
    document.addEventListener('scroll', handleScroll, { capture: true, passive: true });

    // Initial state: assume top of main page until first interaction proves otherwise
    update(true);

    return () => {
      document.removeEventListener('touchstart', handleTouchStart, { capture: true });
      document.removeEventListener('scroll', handleScroll, { capture: true });
      if (rafId !== null) cancelAnimationFrame(rafId);
    };
  }, [sendPullToRefreshEnabled, isInitialized]);
};
